import {
  useUpdatePayrollComponentMutation,
  type PayrollBatchRow,
  type UpdatePayrollComponentBody,
} from '@/app/features/employee/emp-payroll';
import CustomDrawer from '@/components/CustomDrawer';
import { getErrorMessage } from '@/lib/error';
import { toast } from '@/lib/toast';
import { Button, Flex, Form, Input, InputNumber } from 'antd';
import { formatBDT, toAmount } from '../shared/payroll.helpers';
import {
  isSheetGenerated,
  projectNetSalary,
  rowAmounts,
  type RowAdjustments,
} from './batch-payroll.helpers';

type Props = {
  open: boolean;
  /** The batch row being adjusted. */
  row?: PayrollBatchRow | null;
  onClose: () => void;
};

type FormValues = {
  addition: number | null;
  deduction: number | null;
  tax: number | null;
  note?: string;
};

/** The amounts the editor changes, in the order the table shows them. */
const AMOUNT_FIELDS: {
  name: keyof RowAdjustments;
  label: string;
  hint: string;
}[] = [
  { name: 'addition', label: 'Addition', hint: 'Added to the salary' },
  { name: 'deduction', label: 'Deduction', hint: 'Taken off the salary' },
  { name: 'tax', label: 'Tax', hint: 'Taken off the salary' },
];

/**
 * Edits one employee's addition, deduction, tax and note together, with the
 * resulting final salary shown before anything is saved.
 */
const AdjustmentDrawer = ({ open, row, onClose }: Props) => (
  <CustomDrawer
    open={open}
    onClose={onClose}
    title="Edit Adjustments"
    width={450}
  >
    {/* The drawer unmounts its body when closed, so each row starts fresh. */}
    {row && <AdjustmentForm row={row} onDone={onClose} />}
  </CustomDrawer>
);

const AdjustmentForm = ({
  row,
  onDone,
}: {
  row: PayrollBatchRow;
  onDone: () => void;
}) => {
  const [form] = Form.useForm<FormValues>();
  const [updateComponent, { isLoading: saving }] =
    useUpdatePayrollComponentMutation();

  const current = rowAmounts(row);
  const initialNote = row.adjustment_note || '';

  // Live values; before the first render they fall back to the row's own.
  const addition = Form.useWatch('addition', form);
  const deduction = Form.useWatch('deduction', form);
  const tax = Form.useWatch('tax', form);
  const note = Form.useWatch('note', form) ?? initialNote;

  // A cleared box means zero.
  const next: RowAdjustments = {
    addition: toAmount(addition === undefined ? current.addition : addition),
    deduction: toAmount(
      deduction === undefined ? current.deduction : deduction,
    ),
    tax: toAmount(tax === undefined ? current.tax : tax),
  };
  const projected = projectNetSalary(row, next);

  // Only what changed is sent, so the rest of the row is left untouched.
  const body: UpdatePayrollComponentBody = { id: row.id };
  if (next.addition !== current.addition) body.admin_bonus = next.addition;
  if (next.deduction !== current.deduction)
    body.admin_deduction = next.deduction;
  if (next.tax !== current.tax) body.tax = next.tax;
  if (note.trim() !== initialNote.trim()) body.note = note.trim();

  const hasChanges = Object.keys(body).length > 1;
  const isNegative = projected < 0;

  const handleSubmit = async () => {
    // The drawer never opens on a generated row; this also stops a forced submit.
    if (isSheetGenerated(row) || !hasChanges || isNegative) return;
    try {
      await updateComponent(body).unwrap();
      toast('success', 'Adjustments updated successfully');
      onDone();
    } catch (error) {
      toast('error', getErrorMessage(error, 'Failed to update adjustments'));
    }
  };

  return (
    <Form
      form={form}
      size="large"
      layout="vertical"
      className="custom-form space-y-3 [&_.ant-form-item-label]:!pb-1"
      onFinish={handleSubmit}
      initialValues={{
        addition: current.addition,
        deduction: current.deduction,
        tax: current.tax,
        note: initialNote,
      }}
    >
      {/* Who is being adjusted, and the pay the adjustments start from */}
      <div className="rounded-xl border border-theme-gray-700 bg-theme-slate-900 p-4">
        <div className="text-white text-base font-semibold">
          {row.employee_name || 'n/a'}
        </div>
        <div className="text-white/50 text-xs mt-0.5">
          {[row.employee_id, row.department_name].filter(Boolean).join(' · ')}
        </div>
        <Flex justify="space-between" className="mt-4 text-sm">
          <span className="text-white/60">Gross Salary</span>
          <span className="text-white font-semibold">
            {formatBDT(current.gross)}
          </span>
        </Flex>
      </div>

      {AMOUNT_FIELDS.map((field) => (
        <Form.Item
          key={field.name}
          name={field.name}
          // The hint sits beside the label, so it adds no extra line.
          label={
            <span>
              {field.label}
              <span className="text-white/40 text-xs font-normal">
                {' '}
                · {field.hint}
              </span>
            </span>
          }
        >
          <InputNumber
            min={0}
            precision={2}
            controls={false}
            prefix="৳"
            placeholder="0.00"
            className="w-full"
          />
        </Form.Item>
      ))}

      <Form.Item name="note" label="Note">
        <Input.TextArea
          rows={2}
          maxLength={250}
          placeholder="Why the amounts changed (optional)"
        />
      </Form.Item>

      {/* What the employee will be paid once this is saved */}
      <div className="rounded-xl border border-theme-primary/30 bg-theme-primary/10 p-4">
        <div className="text-white/60 text-xs uppercase tracking-wider">
          Final Salary
        </div>
        <Flex align="baseline" gap={10} wrap className="mt-1">
          {hasChanges && (
            <span className="text-white/40 line-through">
              {formatBDT(current.net)}
            </span>
          )}
          <span
            className={
              isNegative
                ? 'text-theme-error-500 text-2xl font-bold'
                : 'text-theme-primary text-2xl font-bold'
            }
          >
            {formatBDT(hasChanges ? projected : current.net)}
          </span>
        </Flex>
        {isNegative && (
          <div className="text-theme-error-500 text-xs mt-1">
            Deductions and tax cannot be more than the salary.
          </div>
        )}
      </div>

      <Flex justify="end" gap={12}>
        <Button
          className="btn-primary-bordered min-w-[80px]"
          onClick={onDone}
          disabled={saving}
        >
          Cancel
        </Button>
        <Button
          htmlType="submit"
          loading={saving}
          disabled={!hasChanges || isNegative}
          className="btn-primary min-w-[80px] h-11 font-semibold"
        >
          Save Changes
        </Button>
      </Flex>
    </Form>
  );
};

export default AdjustmentDrawer;
